import React, { Suspense, lazy } from 'react';

import { Route, Switch, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../Header';

import CallbackPage from '../Callback';
import CompleteMemberSignUp from '../Register/CompleteMemberSignUp';
import ConfirmNewUser from '../Register/ConfirmNewUser';
import NewUserRedirect from '../Register/NewUserRedirect';
import PrivateRoute from '../PrivateRoute';
import userManager from '../../utils/usermanager';
import Footer from '../Footer';
import LoadingSpinner from '../LoadingSpinner';
import '../../App/scss/global.scss';
import styles from './root.module.scss';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import authActions from '../../actions/authActions';
import { getTokenForUser } from '../../utils/helpers';

const ogThumb =
  'https://d84vr99712pyz.cloudfront.net/p/images1/ecri-trusted-voice-healthcare.jpg';

const asyncAnalyticsPage = lazy(() =>
  import('../AnalyticsPage' /* webpackChunkName: "analyticspage" */)
);
const asyncHome = lazy(() => import('../Home' /* webpackChunkName: "home" */));
const asyncBrief = lazy(
  () => import('../Brief') /* webpackChunkName: "brief" */
);

const asyncStaticPage = lazy(() =>
  import('../StaticPage' /* webpackChunkName: "staticpage" */)
);
const asyncAPIStaticPage = lazy(() =>
  import('../APIStaticPage' /* webpackChunkName: "apistaticpage" */)
);
const asyncTrustScore = lazy(() =>
  import('../TrustScore' /* webpackChunkName: "trustscore" */)
);
const asyncSearch = lazy(() =>
  import('../Search' /* webpackChunkName: "search" */)
);
const asyncPageNotFound = lazy(() =>
  import('../PageNotFound' /* webpackChunkName: "pagenotfound" */)
);
const asyncNewContent = lazy(() =>
  import('../NewContent' /* webpackChunkName: "newcontent" */)
);
const asyncRegister = lazy(() =>
  import('../Register' /* webpackChunkName: "register" */)
);
const asyncUMLSIndexingPage = lazy(() =>
  import(
    '../UMLSIndexingPage/SearchGuideline' /* webpackChunkName: "SearchGuideline" */
  )
);
const asyncUMLSIndexingTermsPage = lazy(() =>
  import(
    '../UMLSIndexingPage/SearchUMLSTerms' /* webpackChunkName: "SearchUMLSTerms" */
  )
);
const asyncGuidelineRolesPage = lazy(() =>
  import('../GuidelineRoles/index' /* webpackChunkName: "GuidelineRoles" */)
);

const asyncRegistrationLog = lazy(() =>
  import(
    '../HelpDesk/RegistrationLog/index' /* webpackChunkName: "RegistrationLog" */
  )
);

//Removed for now we may enhance this component later for marketing purposes.
// const asyncGuidelineIndex = lazy(() =>
//   import('../GuidelineIndex/index' /* webpackChunkName: "GuidelineIndex" */)
// );
class Root extends React.Component {
  componentDidUpdate(prevProps) {
    const { user: prevUser } = prevProps;
    const { user: currUser, checkPreviewModeAccess } = this.props;
    if (!prevUser && currUser) {
      // todo eventually we'll use a claim issued from the idp
      checkPreviewModeAccess(currUser);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.user) {
      this.props.getUserRoles();
    }
  }

  handleSearchRequest(phrase) {    
    if (phrase && phrase !== '') {
      this.props.history.push({
        pathname: getTokenForUser(this.props.user) ?'/search':'/public',
        search: encodeURIComponent(`?q=${phrase}`),
        state: { searchPhrase: phrase, sortBy: 'relevance' },
      });
    }
  }

  render() {
    const {
      user,
      isLoadingUser,
      location,
      togglePreviewMode,
      previewMode,
    } = this.props;
    const loggedOut = !user || user.expired;

    let headerClassName = '';

    if (window.location.pathname === '/') {
      headerClassName = 'homepage';
    }

    return (
      <>
        <Helmet title="ECRI Guidelines Trust®">
          <meta name="title" content="ECRI Guidelines Trust®" />
          <meta
            name="description"
            content="ECRI Guidelines Trust® is a publicly available web-based repository of objective, evidence-based clinical practice guideline content."
          />
          <meta
            name="keywords"
            content="Clinical Evidence,
            Clinical Guidelines,
            Clinical Practice Guidelines,
            Clinical Practice Standards,
            Clinical Practices,
            Clinical Recommendations,
            ECRI,
            ECRI Institute,
            Evidence-Based Clinical Practice Guidelines,
            Evidence-Based Guidelines,
            Guidelines Trust,
            Guideline Profile,
            Guideline Repository,
            Guideline Summary,
            Guidelines Library,
            IOM Standards,
            Institute of Medicine,
            National Guideline Clearinghouse,
            NGC,
            NEATS Instrument,
            Systematic Review,
            Systematic Review Guidelines,
            Trust Scorecard,
            Trustworthy Guidelines
            "
          />

          <meta property="og:type" content="article" />
          <meta property="og:site_name" content="ECRI Guidelines Trust®" />
          <meta property="og:title" content="ECRI Guidelines Trust®" />
          {location && location.href && (
            <meta property="og:url" content={location.href} />
          )}
          <meta property="og:image" content={ogThumb} />
          <meta
            property="og:description"
            content="ECRI Guidelines Trust® is a publicly available web-based repository of objective, evidence-based clinical practice guideline content."
          />

          <meta name="twitter:card" content="summary" />
          <meta name="twitter:title" content="ECRI Guidelines Trust®" />
          <meta
            name="twitter:description"
            content="ECRI Guidelines Trust® is a publicly available web-based repository of objective, evidence-based clinical practice guideline content."
          />
          {location && location.href && (
            <meta name="twitter:url" content={location.href} />
          )}
          <meta name="twitter:image" content={ogThumb} />
        </Helmet>

        <Header
          rootUrl={process.env.REACT_APP_PUBLIC_URL}
          userName={loggedOut ? '' : user.profile.name}
          firstName={loggedOut ? '' : user.profile.given_name}
          onSearchRequest={(phrase) => {            
            this.handleSearchRequest(phrase);            
          }}
          loginButtonInfo={{
            mobileText: loggedOut ? 'LOGIN' : 'LOG OUT',
            fullText: loggedOut ? 'Log In' : 'Log Out',
            onClick: () => {
              if (!loggedOut) {
                userManager.createSignoutRequest().then((req) => {
                  window.location = req.url;
                });
              } else {
                userManager.signinRedirect({ state: location });
              }
            },
          }}
          previewButtonInfo={{
            ...previewMode,
            onPreviewChange: (val) => togglePreviewMode(val),
          }}
          cxtClassName={headerClassName}
        />        
        <div className={styles.mainContent}>
          {!isLoadingUser && (
            <Suspense
              fallback={<LoadingSpinner />}
            >
              <Switch>
                <Route exact path="/" component={asyncHome} />
                <Route exact path="/login" component={asyncHome} />
                <Route
                  path="/loggedout"
                  render={() => <h1>You are now logged out.</h1>}
                />
                <Route path="/callback" component={CallbackPage} />
                <Route path="/about" component={asyncStaticPage} />
                <Route path="/inclusion-criteria" component={asyncStaticPage} />                
                {this.props.user ? (
                    <Route
                      path="/about-trust-scorecard"
                      component={asyncStaticPage}
                  />                
                  ):null
                }               
                
                <Route path="/register" component={asyncRegister} />

                <Route path="/confirm-signup" component={ConfirmNewUser} />
                <Route path="/ask-us" component={asyncStaticPage} />
                <Route path="/search-tips" component={asyncStaticPage} />                
                <Route path="/culture" component={asyncStaticPage} />
                <Route path="/submit-guidelines" component={asyncStaticPage} />
                <Route path="/news-1" component={asyncStaticPage} />
                <Route path="/news-2" component={asyncStaticPage} />
                <Route path="/egt-api-data-dictionary" component={asyncAPIStaticPage} />
                <Route path="/egt-api-subscriptions" component={asyncAPIStaticPage} />
                <Route path="/egt-api-overview" component={asyncAPIStaticPage} />
                <Route path="/egt-api-releases" component={asyncAPIStaticPage} />
                <Route path="/egt-api-license" component={asyncAPIStaticPage} />
                <PrivateRoute
                  path="/guidelineroles"
                  component={asyncGuidelineRolesPage}
                />

                <PrivateRoute
                  exact
                  path="/analytics"
                  component={asyncAnalyticsPage}
                />                
                <PrivateRoute
                  exact
                  path="/brief/:uniqueid"
                  component={asyncBrief}
                />                
                <PrivateRoute path="/search" component={asyncSearch} />
                <Route path="/public" component={asyncSearch} />
                                
                <PrivateRoute
                  path="/brief/trustscore/:uniqueid"
                  component={asyncTrustScore}
                />
                <Route
                  exact
                  path="/profile/:uniqueid"
                  component={asyncBrief}
                />                
                <PrivateRoute
                  path="/profile/trustscore/:uniqueid"
                  component={asyncTrustScore}
                />
                <PrivateRoute
                  path="/complete-member-signup"
                  component={CompleteMemberSignUp}
                />
                <PrivateRoute
                  path="/new-user-redirect"
                  component={NewUserRedirect}
                />
                <Route path="/new-content" component={asyncNewContent} />
                <PrivateRoute
                  path="/indexing"
                  component={asyncUMLSIndexingPage}
                />
                <PrivateRoute
                  path="/indexing-terms"
                  component={asyncUMLSIndexingTermsPage}
                />
                <PrivateRoute
                  path="/registrationlog"
                  component={asyncRegistrationLog}
                />
                <Route component={asyncPageNotFound} />

                {/* Removed for now. We may enhance this component later for marketing/SEO purposes  <Route path="/guidelineIndex" component={asyncGuidelineIndex} /> */}
              </Switch>
            </Suspense>
          )}
        </div>
        <Footer
          facebookurl="https://www.facebook.com/ECRIInstitute/"
          twitterurl="https://twitter.com/ECRI_Institute"
          youtubeurl="https://www.youtube.com/user/ECRIInstitute"
          linkedinurl="https://www.linkedin.com/company/ecri-institute"
          chaturl="https://ecrichat.ecri.org/chat/chatstart.aspx?domain=www.ecri.org&link=https://guidelines.ecri.org/ "
          rootUrl={process.env.REACT_APP_PUBLIC_URL}
        />
      </>
    );
  }
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...authActions }, dispatch);

export default withRouter(connect(null, mapDispatchToProps)(Root));
